import React, { useEffect, useState } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  LinearProgress,
  Backdrop,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getCurrentPlan, getSubscriptionStatusInfo } from "screens/store/storeHelper";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { convertToMinSecFormat } from "config/helpers/time_format_helper";
import { LIMITS, formatLicenseData } from "config/helpers/license_helper";
import { getPlanDetailsLabel } from "config/helpers/licensing_helper";
import { useNavigate } from "react-router-dom";
import { GET_USAGE } from "adapters/ehr.service";
import { useDispatch } from "react-redux";
import { fetchUsage } from "redux/actions/PlanUsageActions";
import CommonProgress from "components/loader/commonProgress";
import dayjs from "dayjs";
import { set_show_popup } from "redux/actions/ScribeAcitons";
import { mappedLanguages } from "config/helpers/common_helper";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

const themeEncounter = createTheme({
  palette: {
    primary: {
      main: "rgba(142,146,241,1)",
    },
    secondary: {
      main: "rgba(68,70,161,1)",
    },
  },
});

const themeScribe = createTheme({
  palette: {
    primary: {
      main: "rgba(198,99,129,1)",
    },
    secondary: {
      main: "rgba(156,56,86,1)",
    },
  },
});

const themeMinute = createTheme({
  palette: {
    primary: {
      main: "rgba(185,94,146,1)",
    },
    secondary: {
      main: "rgba(86,29,112,1)",
    },
  },
});

const ParticularsDialog = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const appTheme = useSelector((state) => state.app.theme);
  const licenseObj = useSelector((state) => state?.license?.package);
  const { i18n } = useTranslation();
  const currentLicense = LIMITS?.[licenseObj?.license];
  const [licenseDetails, setLicenseDetails] = useState([]);
  const navigate = useNavigate();
  const theme = useSelector((state) => state?.app?.theme);
  const reduxDispatch = useDispatch();
  const planUsageObj = useSelector((state) => state.planUsage.usagePayload);
  const planUsageLoading = useSelector((state) => state.planUsage.loading);
  const isTab = useMediaQuery("(max-width:1365px)");

  const subscriptionStatusInfo = getSubscriptionStatusInfo();

  useEffect(() => {
    reduxDispatch(fetchUsage());
  }, [reduxDispatch]);

  useEffect(() => {
    if (currentLicense && planUsageObj) {
      const planName = licenseObj?.license.toLowerCase();
      const details = formatLicenseData(currentLicense, planUsageObj, planName);
      setLicenseDetails(details);
    }
  }, [currentLicense, planUsageObj]);

  const getGradientColor = (percentage, theme) => {
    if (percentage <= 40) {
      return `linear-gradient(180deg, ${theme.palette.primary?.main} 0%, ${theme.palette.secondary?.main} 80%)`;
    } else if (percentage <= 80) {
      return ` linear-gradient(180deg, #FFCC65 0%, #BD7F00 100%)`; // warning orange gradient
    } else {
      return `linear-gradient(180deg, #FF6773 0%, #BE3741 100%);`; // red gradient
    }
  };

  const GradientLinearProgress = styled(LinearProgress)(
    ({ theme, percentage }) => ({
      height: "clamp(2.5vh, 2.7vw, 2.7vw)",
      borderRadius: 15,
      backgroundColor: appTheme === "dark" ? "rgba(38,35,68)" : "#C7C8CC",
      "& .MuiLinearProgress-bar": {
        height: "100%",
        borderRadius: 15,
        background: getGradientColor(percentage, theme),
      },
    })
  );

  const LabeledProgressBar = ({ label, value, theme, count }) => {
    console.log("value", value, "label", label);
    console.log("count", count);

    const isVoiceAssistant = label?.includes("Hey, Noki (AI Voice Assistant)");
    const isConversations = label?.includes("Conversations");

    const adjustedValue = isVoiceAssistant ? value / 60000 : value;
    const percentage =  isVoiceAssistant? (Number(adjustedValue) * 100)?.toFixed(3) / count: Math.floor((Number(adjustedValue) * 100) / count);

    console.log("percentage", percentage, label);

    const displayValue = isVoiceAssistant
      ? convertToMinSecFormat(adjustedValue)
      : value;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            component="span"
            fontFamily="inherit"
            textAlign={"start"}
            sx={{
              fontSize: "clamp(1.8vh, 0.8vw, 0.8vw)",
            }}
          >
            {isConversations && count === "Infinity"
              ? i18n.t("dashboard2.LIMITS_UNLIMITED_CONVERSATION")
              : label === "Conversations"
              ? i18n.t("dashboard2.LIMITS_CONVERSATION")
              : label?.includes?.("Hey, Noki (AI Voice Assistant)")
                ? i18n.t("dashboard2.LIMITS_HEYNOKI")
                : label === "CDSS"
                ? i18n.t("dashboard2.LIMITS_CDSS")
                : label === "ICD CPT Codes"
                ? i18n.t("dashboard2.LIMITS_ICD_CPT")
                : label}
          </Typography>
          {count === "Infinity" ? (
            isConversations ? (
              <Typography
                variant="body1"
                component="span"
                className="dashboard-text-sm"
                fontFamily="inherit"
                textAlign={"end"}
                sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
              >
                {value}/<AllInclusiveIcon sx={{ fontSize: "1rem" }} />
              </Typography>
            ) : (
              <Typography
                variant="body1"
                component="span"
                className="dashboard-text-sm"
                fontFamily="inherit"
                textAlign={"end"}
              >
                {i18n.t("common.UNLIMITED")}
              </Typography>
            )
          ) : count != 0 && (
            <Typography
              variant="body1"
              component="span"
              className="ms-2"
              fontFamily="inherit"
              textAlign={"end"}
              sx={{
                fontSize: "clamp(1.8vh, 0.8vw, 0.8vw)",
              }}
            >
              ({displayValue}/{count} {isVoiceAssistant ? " mins" : ""}{" "}
              {i18n.t("dashboard2.USED")})
            </Typography>
          )}
        </Box>
        {
          count == 0 ?
            <Typography
              variant="body1"
              component="span"
              fontFamily="inherit"
              textAlign={"start"}
              sx={{
                fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
              }}
              className="text-danger b-600"
            >
              {i18n.t("license_messages.FEATURE_UNAVAILABLE")}
            </Typography> :
            <GradientLinearProgress
              variant="determinate"
              value={percentage}
              percentage={percentage}
            />
        }
      </Box>
    );
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    reduxDispatch(fetchUsage());
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  const getProgressValue = (feature, value, limit) => {
    if (feature?.includes("Hey, Noki (AI Voice Assistant)")) {
      return value;
    }
    return Number(value) > Number(limit) ? limit : value;
  };

  return (
    <>
        <Box className="d-flex flex-column gap-3 p-3">

          {!planUsageLoading && (
            <Typography
              sx={{
                color: appTheme === "dark" ? "#B6B6FF" : "#000",
                fontSize: "1rem",
              }}
            >
              {getPlanDetailsLabel(i18n)}
            </Typography>
          )}

          {!planUsageLoading && subscriptionStatusInfo?.messageKey && (
            <Box
              className="border-bottom pb-2"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 1.5,
              }}
            >
              {subscriptionStatusInfo.hasIssue && subscriptionStatusInfo.iconType === "error" && (
                subscriptionStatusInfo.statusType === "payment_failed" ? (
                  <CreditCardOffIcon sx={{ fontSize: "2.5rem", color: "#FF6773" }} />
                ) : (
                  <EventBusyIcon sx={{ fontSize: "2.5rem", color: "#FF6773" }} />
                )
              )}
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  color: subscriptionStatusInfo.hasIssue ? "#FF6773" : "inherit",
                  fontWeight: subscriptionStatusInfo.hasIssue ? 600 : 400,
                }}
              >
                {i18n.t(subscriptionStatusInfo.messageKey)}{" "}
                {subscriptionStatusInfo.daysRemaining != null ? (
                  <Box
                    component="span"
                    sx={{ color: "#FF6773", fontWeight: 600 }}
                  >
                    {subscriptionStatusInfo.daysRemaining}{" "}
                    {subscriptionStatusInfo.daysRemaining === 1
                      ? i18n.t("dashboard2.DAY")
                      : i18n.t("dashboard2.DAYS")}
                  </Box>
                ) : (
                  subscriptionStatusInfo.date && (
                    <Box
                      component="span"
                      sx={{ color: "#FF6773", fontWeight: 600 }}
                    >
                      {dayjs(subscriptionStatusInfo.date).format("MM/DD/YYYY")}
                    </Box>
                  )
                )}
              </Typography>
            </Box>
          )}

          <Box
            className="d-flex flex-column gap-4 py-2"
            sx={{ width: "100%", minHeight: "100px" }}
          >
            {planUsageLoading ? (
              <div
                className="d-flex justify-content-center align-items-center mt-5"
                style={{ height: "100%" }}
              >
                <CommonProgress />
              </div>
            ) : (
              licenseDetails?.map((ele, index) => (
                <LabeledProgressBar
                  key={index}
                  label={ele?.feature}
                  // value={Number(ele?.value) > Number(ele?.limit) ? ele?.limit : ele?.value}
                  value={getProgressValue(ele?.feature, ele?.value, ele?.limit)}
                  theme={theme}
                  count={ele?.limit}
                />
              ))
            )}
            {!planUsageLoading &&
              licenseObj?.license.toLowerCase() === "trial" && (
                <Box>
                  <Typography
                    sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                  >
                    {i18n.t("license_messages.TRIAL_WARNING_INFO")}
                  </Typography>
                </Box>
              )}
          </Box>

        </Box>
    </>
  );
};

export default ParticularsDialog;
